import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Button, CircularProgress,
    FormControl, InputLabel, Select, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { useAdminUser, useUpdateUser, useDeleteUser, useReactivateUser } from '../../hooks/useUsers';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import PortalPicker from '../PortalPicker';
import { formatDate } from './adminUtils';

const SectionHeader = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
        <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
        >
            {title}
        </Typography>
    </Box>
);

export default function AdminUserDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: adminUser } = useAuth();
    const { showError, showSuccess } = useNotification();
    const { data: userData, isLoading } = useAdminUser(id);
    const updateUser = useUpdateUser();
    const deleteUser = useDeleteUser();
    const reactivateUser = useReactivateUser();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [alertLimit, setAlertLimit] = useState(0);
    const [ssLimit, setSsLimit] = useState(0);
    const [briefingLimit, setBriefingLimit] = useState(0);
    const [smartAlertLimit, setSmartAlertLimit] = useState(0);
    const [locale, setLocale] = useState('hr');
    const [selectedPortals, setSelectedPortals] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => { document.title = userData ? `${userData.username} | Media Scanner` : 'Korisnik | Media Scanner'; }, [userData]);

    useEffect(() => {
        if (!userData || loaded) return;
        setUsername(userData.username);
        setAlertLimit(userData.alert_limit);
        setSsLimit(userData.scheduled_searches_limit);
        setBriefingLimit(userData.briefing_limit);
        setSmartAlertLimit(userData.smart_alert_limit ?? 0);
        setLocale(userData.locale);
        setSelectedPortals((userData.allowed_portals || []).map(p => ({ id: p.id, name: p.name })));
        setLoaded(true);
    }, [userData, loaded]);

    const hasChanges = userData && loaded && (
        username !== userData.username ||
        password !== '' ||
        alertLimit !== userData.alert_limit ||
        ssLimit !== userData.scheduled_searches_limit ||
        briefingLimit !== userData.briefing_limit ||
        smartAlertLimit !== (userData.smart_alert_limit ?? 0) ||
        locale !== userData.locale ||
        JSON.stringify(selectedPortals.map(p => p.id).sort()) !== JSON.stringify((userData.allowed_portals || []).map(p => p.id).sort())
    );

    const handleSave = () => {
        if (!username.trim()) { showError('Korisničko ime je obavezno'); return; }
        setSaving(true);

        const payload = {
            id: parseInt(id),
            username: username.trim(),
            alert_limit: alertLimit,
            scheduled_searches_limit: ssLimit,
            briefing_limit: briefingLimit,
            smart_alert_limit: smartAlertLimit,
            locale: locale,
            allowed_portal_ids: selectedPortals.map(p => p.id),
        };
        if (password) payload.password = password;

        updateUser.mutate(payload, {
            onSuccess: () => {
                showSuccess('Korisnik uspješno ažuriran');
                setSaving(false);
                setPassword('');
                setLoaded(false);
            },
            onError: (err) => {
                setSaving(false);
                if (err.response?.status === 409) {
                    showError('Korisničko ime već postoji');
                } else {
                    showError('Greška pri ažuriranju korisnika');
                }
            },
        });
    };

    const handleDeactivate = () => {
        deleteUser.mutate(parseInt(id), {
            onSuccess: () => {
                showSuccess('Korisnik deaktiviran');
                setConfirmOpen(false);
                navigate('/admin/users');
            },
            onError: () => {
                setConfirmOpen(false);
                showError('Greška pri deaktivaciji korisnika');
            },
        });
    };

    const handleReactivate = () => {
        reactivateUser.mutate(parseInt(id), {
            onSuccess: () => {
                showSuccess('Korisnik reaktiviran');
                setLoaded(false);
            },
            onError: () => showError('Greška pri reaktivaciji korisnika'),
        });
    };

    if (isLoading || !userData) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    const isAdminUser = userData.id === adminUser?.user_id;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                        {userData.username}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 600, fontSize: '0.8rem',
                            backgroundColor: userData.is_active ? '#e8f5e9' : '#f5f5f5',
                            color: userData.is_active ? '#2e7d32' : '#999',
                        }}
                    >
                        {userData.is_active ? 'Aktivan' : 'Neaktivan'}
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                    Kreiran: {formatDate(userData.timedate_created)} · Zadnja promjena: {formatDate(userData.timedate_changed)}
                </Typography>

                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <SectionHeader title="Pristupni podaci" />
                    <TextField
                        label="Korisničko ime"
                        fullWidth
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        sx={{ mb: 2 }}
                        size="small"
                    />
                    <TextField
                        label="Nova lozinka"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        size="small"
                        placeholder="Ostavi prazno za zadržati trenutnu lozinku"
                    />

                    <SectionHeader title="Limiti" />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Alert limit"
                            type="number"
                            value={alertLimit}
                            onChange={e => setAlertLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Auto. pretrage limit"
                            type="number"
                            value={ssLimit}
                            onChange={e => setSsLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Briefing limit"
                            type="number"
                            value={briefingLimit}
                            onChange={e => setBriefingLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Smart alert limit"
                            type="number"
                            value={smartAlertLimit}
                            onChange={e => setSmartAlertLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                    </Box>

                    <SectionHeader title="Jezik" />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Jezik</InputLabel>
                        <Select value={locale} label="Jezik" onChange={e => setLocale(e.target.value)}>
                            <MenuItem value="hr">Hrvatski</MenuItem>
                            <MenuItem value="si">Slovenski</MenuItem>
                            <MenuItem value="ro">Rumunjski</MenuItem>
                            <MenuItem value="ba">Bosanski</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                        </Select>
                    </FormControl>

                    <SectionHeader title="Dozvoljeni portali" />
                    <PortalPicker value={selectedPortals} onChange={setSelectedPortals} autoDetectForeign />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                            {userData.is_active && !isAdminUser && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setConfirmOpen(true)}
                                    sx={{ textTransform: 'none', borderRadius: '20px' }}
                                >
                                    Deaktiviraj korisnika
                                </Button>
                            )}
                            {!userData.is_active && (
                                <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={handleReactivate}
                                    sx={{ textTransform: 'none', borderRadius: '20px' }}
                                >
                                    Reaktiviraj korisnika
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/admin/users')}
                                sx={{ textTransform: 'none', borderRadius: '20px' }}
                            >
                                Natrag na listu
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                sx={{ textTransform: 'none', borderRadius: '20px', minWidth: 140 }}
                            >
                                {saving ? <CircularProgress size={20} /> : 'Spremi promjene'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Deaktivacija korisnika</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Jeste li sigurni da želite deaktivirati korisnika <strong>{userData.username}</strong>?
                        Korisnik se neće moći prijaviti.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>Odustani</Button>
                    <Button onClick={handleDeactivate} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Deaktiviraj
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
